import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { db } from './firebase';

const RC_PROJECT_ID = 'b99df218';

export function revenueCatUrl(userId) {
  return `https://app.revenuecat.com/projects/${RC_PROJECT_ID}/customers/${userId}?tab=overview`;
}

/** Load and merge beta request data from three Firestore sources. */
export async function loadBetaRequests() {
  const [interestSnap, submissionSnap, testerSnap] = await Promise.all([
    getDocs(collection(db, 'interest_registrations')),
    getDocs(
      query(
        collection(db, 'submissions', '_index', 'feedback'),
        where('category', '==', 'beta_access'),
      ),
    ),
    getDocs(collection(db, 'beta_testers')),
  ]);

  const users = {};

  function ensureUser(uid) {
    if (!users[uid]) {
      users[uid] = {
        user_id: uid,
        email: '',
        club: '',
        platform: '',
        earliest: null,
        interest: false,
        inApp: false,
        granted: false,
        tier: '',
      };
    }
    return users[uid];
  }

  function trackDate(u, ts) {
    if (ts && (!u.earliest || ts < u.earliest)) u.earliest = ts;
  }

  // 1. interest_registrations
  interestSnap.forEach((d) => {
    const data = d.data();
    const uid = data.user_id || data.user_email;
    if (!uid) return;
    const u = ensureUser(uid);
    u.interest = true;
    if (data.user_email) u.email = data.user_email;
    if (data.club_id) u.club = data.club_id;
    const plat = [];
    if (data.platform_ios === 1 || data.platform === 'ios' || data.platform === 'both') plat.push('iOS');
    if (data.platform_android === 1 || data.platform === 'android' || data.platform === 'both') plat.push('Android');
    if (plat.length && !u.platform) u.platform = plat.join(', ');
    if (data.registered_at) trackDate(u, data.registered_at.toDate());
  });

  // 2. submissions feedback (beta_access)
  submissionSnap.forEach((d) => {
    const data = d.data();
    const uid = data.user_id;
    if (!uid) return;
    const u = ensureUser(uid);
    u.inApp = true;
    if (data.user_email && !u.email) u.email = data.user_email;
    if (data.platform && !u.platform) u.platform = data.platform;
    if (data.created_at) trackDate(u, data.created_at.toDate());
  });

  // 3. beta_testers (granted users)
  testerSnap.forEach((d) => {
    const data = d.data();
    const expired = data.expires_at && data.expires_at.toDate() < new Date();
    if (expired) return;
    const u = ensureUser(d.id);
    u.granted = true;
    u.tier = data.tier || 'navigator';
    if (data.granted_at) trackDate(u, data.granted_at.toDate());
  });

  const rows = Object.values(users);
  rows.sort((a, b) => {
    if (!a.earliest) return 1;
    if (!b.earliest) return -1;
    return b.earliest - a.earliest;
  });

  return rows;
}

// ── Grant cleanup: engagement + profile completeness (ra-4e38) ──────
//
// The granted table is a CLEANUP surface — "which of these grants is dead?" —
// and answering that needs auth sign-in times, race history, device check-ins
// and profile state. None of it is readable from here: `users/**` is owner-only
// and `devices` deliberately has no admin read arm. Chris' directive of
// 2026-08-11 (on ra-e9yl.1) is firm that the committee app must NOT gain new
// direct Firestore read paths for user profiles or activity, so the whole walk
// lives in the admin-gated `getBetaTesterInsights` callable and the web only
// renders what it returns. See functions/beta_tester_insights.js. The pure
// half — staleness, sorting, the merge — is in betaInsights.js.

/**
 * Fetch per-user engagement + profile insights for the given uids.
 * Returns a Map keyed by uid so callers can merge without re-scanning.
 */
export async function loadBetaTesterInsights(userIds) {
  const uids = [...new Set((userIds || []).filter((u) => typeof u === 'string' && u.trim()))];
  if (uids.length === 0) return new Map();
  const fn = httpsCallable(functions, 'getBetaTesterInsights');
  const result = await fn({ uids });
  const insights = (result?.data?.insights) || [];
  return new Map(insights.map((row) => [row.uid, row]));
}

export async function grantBetaAccess(userId) {
  await setDoc(doc(db, 'beta_testers', userId), {
    tier: 'navigator',
    granted_at: serverTimestamp(),
    granted_by: 'admin',
  });
}

export async function revokeBetaAccess(userId) {
  await deleteDoc(doc(db, 'beta_testers', userId));
}
